/**
 * Selected-chip content for the interviewer Select — avatar + name on
 * the left, timezone clock on the right. Used via
 * `renderTriggerValue` on the async Select.
 */
import { Flex, Text } from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { InterviewerCalNotConnected } from './InterviewerCalNotConnected';
import { InterviewerOutlookOwner } from './InterviewerOutlookOwner';
import { InterviewerTimezoneTrigger } from '../../timezoneModal';

interface InterviewerTriggerChipProps {
  label: string;
  image?: string;
  /** Pre-formatted "10:30 AM PST" clock; empty when tz is unknown. */
  clock?: string;
  /** When true, renders a warning icon before the avatar with a hover
   *  tooltip explaining the interviewer's calendar isn't connected. */
  calendarNotConnected?: boolean;
  /** Outlook only: interviewer owns the selected shared calendar.
   *  Renders the AlertTriangle warning before the avatar (supersedes
   *  the globe / disconnected icons for this chip). */
  isOutlookOwner?: boolean;
  /** Fires when the user clicks "Set timezone" on the missing-tz popover. */
  onSetTimezone?: () => void;
}

export function InterviewerTriggerChip({
  label,
  image,
  clock,
  calendarNotConnected,
  isOutlookOwner,
  onSetTimezone,
}: InterviewerTriggerChipProps) {
  return (
    <Flex align="center" gap="2" w="100%" minW={0}>
      {isOutlookOwner && <InterviewerOutlookOwner />}
      {!clock && onSetTimezone && !isOutlookOwner && (
        <InterviewerTimezoneTrigger onSetTimezone={onSetTimezone} />
      )}
      {calendarNotConnected && !isOutlookOwner && (
        <InterviewerCalNotConnected
          size={20}
          color="var(--kula-colors-error-200)"
        />
      )}
      <Avatar src={image} name={label} size="xs" shape="square" />
      <Text
        fontSize="sm"
        color={calendarNotConnected ? 'error-200' : 'secondary-900'}
        fontWeight="medium"
        noOfLines={1}
        flex="1"
        minW={0}
      >
        {label}
      </Text>
      {clock ? (
        <Text
          fontSize="xs"
          color={calendarNotConnected ? 'error-200' : 'secondary-500'}
          flexShrink={0}
        >
          {clock}
        </Text>
      ) : null}
    </Flex>
  );
}
